import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(null); // 'admin' or 'presenter'
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check authentication status on app load
  useEffect(() => {
    checkAuthStatus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const checkAuthStatus = async () => {
    try {
      setLoading(true);

      // Check if token is valid
      const tokenValid = await authAPI.verifyToken();

      if (tokenValid) {
        // Get user info and type from localStorage or API
        const storedUserType = localStorage.getItem('userType');
        const storedUserInfo = localStorage.getItem('userInfo');

        // If no stored info, try to get from API
        try {
          const response = await authAPI.getCurrentUser();
          setUser(response.data.user);
          setUserType(response.data.userType);
          setIsAuthenticated(true);

          // Store in localStorage for persistence
          localStorage.setItem('userType', response.data.userType);
          localStorage.setItem('userInfo', JSON.stringify(response.data.user));
        } catch (error) {
          console.log('Could not fetch user info');
          // Just clear the state without redirecting during app initialization
          clearAuthState();
        }
      } else {
        // Just clear the state without redirecting during app initialization
        clearAuthState();
      }
    } catch (error) {
      console.log('Auth check failed:', error);
      // Just clear the state without redirecting during app initialization
      clearAuthState();
    } finally {
      setLoading(false);
    }
  };

  const login = async (credentials, type) => {
    try {
      setLoading(true);

      let response;
      if (type === 'admin') {
        response = await authAPI.loginAdmin(credentials);
      } else {
        response = await authAPI.loginPresenter(credentials);
      }

      // Set user state
      setUser(response.data.user);
      setUserType(type);
      setIsAuthenticated(true);

      // Store in localStorage
      localStorage.setItem('userType', type);
      localStorage.setItem('userInfo', JSON.stringify(response.data.user));

      return response;
    } catch (error) {
      // The error is already formatted with user-friendly messages by the API service
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Clear authentication state without redirecting
  const clearAuthState = () => {
    setUser(null);
    setUserType(null);
    setIsAuthenticated(false);
    localStorage.removeItem('userType');
    localStorage.removeItem('userInfo');
  };

  const logout = async () => {
    try {
      // Call logout API to invalidate server-side session
      await authAPI.logout();
    } catch (error) {
      console.log('Logout API call failed:', error);
    } finally {
      clearAuthState();
    }
  };

  // Smart logout function that redirects based on user type
  const smartLogout = async () => {
    // Store the current user type before clearing it
    const currentUserType = userType;
    
    try {
      // Call logout API to invalidate server-side session
      await authAPI.logout();
    } catch (error) {
      console.log('Logout API call failed:', error);
    } finally {
      // Clear authentication state
      clearAuthState();
      
      // Redirect to appropriate login page based on user type
      if (currentUserType === 'admin') {
        window.location.href = '/admin-login';
      } else if (currentUserType === 'presenter') {
        window.location.href = '/presenter-login';
      } else {
        // Fallback to landing page if no user type
        window.location.href = '/';
      }
    }
  };

  const refreshAuth = async () => {
    await checkAuthStatus();
  };

  const value = {
    user,
    userType,
    isAuthenticated,
    loading,
    login,
    logout,
    smartLogout,
    clearAuthState,
    refreshAuth,
    checkAuthStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext; 